import React, { useEffect, useState } from "react";
import { Play, FileSpreadsheet, FileText, Package, Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import api, { API_BASE, formatErr } from "../lib/api";
import SurveyAndTier2Tab from "./SurveyAndTier2";
import InfraReV3Panel from "./InfraReV3Panel";

/** SimulationsTab — manages RE & Infrastructure simulation runs and snapshot exports.
 *
 * Backend ready endpoints:
 *   POST /api/projects/{id}/re-simulation/run
 *   POST /api/projects/{id}/infra-simulation/run
 *   GET  /api/re-simulation/schema
 *   GET  /api/infra-simulation/schema
 *   GET  /api/projects/{id}/re-simulation/snapshot.{xlsx,pdf}
 *   GET  /api/projects/{id}/infra-simulation/snapshot.{xlsx,pdf}
 *   GET  /api/projects/{id}/issue-for-construction.zip
 */
async function downloadAuthed(url, filename) {
  const res = await fetch(url, { credentials: "include" });
  if (!res.ok) {
    const txt = await res.text().catch(() => res.statusText);
    throw new Error(`HTTP ${res.status} — ${txt}`);
  }
  const blob = await res.blob();
  const href = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = href;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    URL.revokeObjectURL(href);
    a.remove();
  }, 1000);
}

function PanelHeader({ title, subtitle, status }) {
  return (
    <div className="flex items-start justify-between">
      <div>
        <div className="pf-h2" style={{ fontSize: 18 }}>{title}</div>
        <div className="pf-muted" style={{ fontSize: 13 }}>{subtitle}</div>
      </div>
      {status}
    </div>
  );
}

function StatusPill({ kind, children }) {
  const map = {
    ok: { bg: "rgba(46,125,50,0.15)", fg: "#7AC07F", icon: CheckCircle2 },
    err: { bg: "rgba(198,40,40,0.15)", fg: "#FF8888", icon: AlertCircle },
    pending: { bg: "rgba(242,107,31,0.15)", fg: "#F26B1F", icon: Loader2 },
  };
  const s = map[kind] || map.pending;
  const Icon = s.icon;
  return (
    <span style={{
      background: s.bg, color: s.fg, padding: "4px 10px",
      borderRadius: 999, fontSize: 12, display: "inline-flex",
      gap: 6, alignItems: "center", fontWeight: 600,
    }}>
      <Icon size={14} className={kind === "pending" ? "animate-spin" : ""} />
      {children}
    </span>
  );
}

function DownloadCard({ label, sublabel, icon: Icon, onClick, testid, disabled }) {
  return (
    <button
      data-testid={testid}
      disabled={disabled}
      onClick={onClick}
      className="pf-card-button"
      style={{
        display: "flex", alignItems: "center", gap: 14,
        padding: 14, border: "1px solid var(--pf-border)",
        borderRadius: 8, background: "var(--pf-surface-2)",
        textAlign: "left", cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.45 : 1, width: "100%",
        transition: "transform 120ms, border-color 120ms",
      }}
      onMouseEnter={(e) => !disabled && (e.currentTarget.style.borderColor = "var(--pf-ember)")}
      onMouseLeave={(e) => !disabled && (e.currentTarget.style.borderColor = "var(--pf-border)")}
    >
      <div style={{
        width: 40, height: 40, borderRadius: 8,
        background: "rgba(242,107,31,0.12)", display: "grid",
        placeItems: "center", color: "#F26B1F",
      }}>
        <Icon size={20} />
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 600, fontSize: 14 }}>{label}</div>
        <div className="pf-muted" style={{ fontSize: 12 }}>{sublabel}</div>
      </div>
    </button>
  );
}

function SimulationPanel({ projectId, kind, title, subtitle, schemaUrl, runUrl, snapshotBase }) {
  const [running, setRunning] = useState(false);
  const [err, setErr] = useState(null);
  const [last, setLast] = useState(null);

  const runWithSample = async () => {
    setRunning(true);
    setErr(null);
    try {
      const { data: schema } = await api.get(schemaUrl);
      const { data: result } = await api.post(runUrl, schema.sample_payload || {});
      const sections = Object.keys(result?.report?.sections || {});
      setLast({ id: result.id, sections, when: result.created_at });
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail) || String(e));
    } finally {
      setRunning(false);
    }
  };

  const dl = async (ext) => {
    try {
      await downloadAuthed(`${API_BASE}${snapshotBase}.${ext}`,
        `${kind}_Snapshot_${(last?.id || "latest")}.${ext}`);
    } catch (e) {
      setErr(String(e));
    }
  };

  return (
    <div style={{
      border: "1px solid var(--pf-border)", borderRadius: 12,
      padding: 18, background: "var(--pf-surface)",
      display: "flex", flexDirection: "column", gap: 14,
    }} data-testid={`sim-panel-${kind}`}>
      <PanelHeader
        title={title}
        subtitle={subtitle}
        status={
          running ? <StatusPill kind="pending">Running…</StatusPill> :
          err ? <StatusPill kind="err">Error</StatusPill> :
          last ? <StatusPill kind="ok">{last.sections.length} section{last.sections.length === 1 ? "" : "s"}</StatusPill> :
          null
        }
      />

      <button
        data-testid={`sim-run-${kind}`}
        onClick={runWithSample}
        disabled={running}
        className="pf-btn pf-btn-primary"
        style={{
          padding: "10px 16px", fontSize: 14, fontWeight: 600,
          display: "inline-flex", alignItems: "center", gap: 8,
          width: "fit-content",
        }}
      >
        {running ? <Loader2 size={16} className="animate-spin" /> : <Play size={16} />}
        Run with Sample Payload
      </button>

      {err && (
        <div data-testid={`sim-err-${kind}`} style={{
          padding: 10, borderRadius: 6,
          background: "rgba(198,40,40,0.1)",
          border: "1px solid rgba(198,40,40,0.3)",
          color: "#FF8888", fontSize: 12,
        }}>{err}</div>
      )}

      {last && (
        <div data-testid={`sim-last-${kind}`} className="pf-muted" style={{ fontSize: 12 }}>
          Last run <code style={{ background: "var(--pf-surface-2)", padding: "1px 6px", borderRadius: 4 }}>{last.id}</code>
          {" — sections: "}{last.sections.join(", ") || "(none)"}
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <DownloadCard
          label="Snapshot · XLSX"
          sublabel="Multi-sheet workbook with code references"
          icon={FileSpreadsheet}
          testid={`sim-dl-xlsx-${kind}`}
          onClick={() => dl("xlsx")}
        />
        <DownloadCard
          label="Snapshot · PDF"
          sublabel="Stamped A4 engineering report"
          icon={FileText}
          testid={`sim-dl-pdf-${kind}`}
          onClick={() => dl("pdf")}
        />
      </div>
    </div>
  );
}

function IFCBundleCard({ projectId }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const dl = async () => {
    setBusy(true);
    setErr(null);
    try {
      await downloadAuthed(
        `${API_BASE}/projects/${projectId}/issue-for-construction.zip`,
        `IssueForConstruction_${projectId.slice(0, 8)}.zip`);
    } catch (e) {
      setErr(String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="ifc-bundle-card" style={{
      border: "1px solid var(--pf-ember)", borderRadius: 12,
      padding: 18, background: "linear-gradient(135deg, rgba(242,107,31,0.08), rgba(31,58,95,0.08))",
      display: "flex", flexDirection: "column", gap: 12,
    }}>
      <div style={{ display: "flex", gap: 14, alignItems: "center" }}>
        <div style={{
          width: 48, height: 48, borderRadius: 10,
          background: "var(--pf-ember)", color: "#fff",
          display: "grid", placeItems: "center",
        }}>
          <Package size={24} />
        </div>
        <div style={{ flex: 1 }}>
          <div className="pf-h2" style={{ fontSize: 17 }}>Issue-For-Construction Bundle</div>
          <div className="pf-muted" style={{ fontSize: 13 }}>
            Single ZIP · RE Snapshot (XLSX+PDF+JSON) + Infra Snapshot (XLSX+PDF+JSON) +
            3D Models (glTF + IFC 4) + signed manifest with SHA-256 digests.
          </div>
        </div>
      </div>
      <button
        data-testid="ifc-bundle-download"
        onClick={dl}
        disabled={busy}
        className="pf-btn pf-btn-primary"
        style={{
          padding: "10px 16px", fontSize: 14, fontWeight: 600,
          display: "inline-flex", alignItems: "center", gap: 8,
          width: "fit-content",
        }}
      >
        {busy ? <Loader2 size={16} className="animate-spin" /> : <Package size={16} />}
        Download Bundle
      </button>
      {err && (
        <div data-testid="ifc-bundle-err" style={{
          padding: 10, borderRadius: 6,
          background: "rgba(198,40,40,0.1)",
          border: "1px solid rgba(198,40,40,0.3)",
          color: "#FF8888", fontSize: 12,
        }}>{err}</div>
      )}
    </div>
  );
}

export default function SimulationsTab({ projectId, mode = "all", domain = "infrastructure" }) {
  const showRE = mode === "all" || mode === "re";
  const showInfra = mode === "all" || mode === "infra";
  const showDeliverables = mode === "all" || mode === "deliverables";
  const showSurvey = mode === "all" || mode === "survey";
  const showIFCBundle = mode === "all" || mode === "deliverables";

  const titleByMode = {
    deliverables: "Project Deliverables",
    re: "Real-Estate Simulation",
    infra: "Infrastructure Simulation",
    survey: "Survey · GIS · EIA · Value Engineering",
    all: "Engineering Simulations",
  };
  const subtitleByMode = {
    deliverables: "BOQ · CPM Schedule · Tender Documents · Rebar BBS · Issue-For-Construction Bundle",
    re: "Structural · HVAC · Plumbing · Electrical · Fire · Daylight · Acoustic — 69 deterministic routines",
    infra: "Traffic · Hydraulics · Geotech · Bridge · Pavement · Earthwork · Noise — ~50 routines",
    survey: "TIN/DTM · Cross-sections · Specs · EIA · Lifecycle Cost · Pavement Design",
    all: "Run deterministic Real-Estate or Infrastructure simulations and download stamped engineering deliverables.",
  };

  return (
    <div data-testid={`simulations-tab-${mode}`} style={{
      display: "flex", flexDirection: "column", gap: 18,
      maxWidth: mode === "deliverables" || mode === "survey" || mode === "all" ? 1100 : "none",
      width: "100%",
    }}>
      <div>
        <div className="pf-h1">{titleByMode[mode] || titleByMode.all}</div>
        <div className="pf-muted" style={{ fontSize: 14 }}>{subtitleByMode[mode] || subtitleByMode.all}</div>
      </div>

      {showRE && (
        <>
          <InfraReV3Panel domain="real_estate" projectId={projectId} />
          <SimulationPanel
            projectId={projectId}
            kind="re"
            title="Real-Estate Simulation (legacy snapshots)"
            subtitle="Run with sample payload + download stamped snapshots."
            schemaUrl="/re-simulation/schema"
            runUrl={`/projects/${projectId}/re-simulation/run`}
            snapshotBase={`/projects/${projectId}/re-simulation/snapshot`}
          />
        </>
      )}
      {showInfra && (
        <>
          <InfraReV3Panel domain="infrastructure" projectId={projectId} />
          <SimulationPanel
            projectId={projectId}
            kind="infra"
            title="Infrastructure Simulation (legacy snapshots)"
            subtitle="Run with sample payload + download stamped snapshots."
            schemaUrl="/infra-simulation/schema"
            runUrl={`/projects/${projectId}/infra-simulation/run`}
            snapshotBase={`/projects/${projectId}/infra-simulation/snapshot`}
          />
        </>
      )}

      {showIFCBundle && <IFCBundleCard projectId={projectId} />}
      {showDeliverables && <Tier1Panel projectId={projectId} domain={domain} />}
      {showSurvey && <SurveyAndTier2Tab projectId={projectId} />}
    </div>
  );
}

/* ─── Phase 16 — Project Deliverables panel (BOQ / Schedule / Tender / BBS) ─── */
const DELIVERABLE_DEFAULTS = {
  process_plant: {
    boq: [
      { code: "STEEL_STRUCT", qty: 80, source: "3D" },
      { code: "PIPE_CS_8IN", qty: 250, source: "3D" },
      { code: "PUMP_CENTRIFUGAL", qty: 6, source: "3D" },
      { code: "RCC_M30_PLINTH", qty: 60, source: "3D" },
      { code: "EXC_HARD", qty: 120, source: "DRAWING" },
      { code: "REBAR_FE500", qty: 8, source: "3D" },
    ],
    bbs: [
      { bar_mark: "P1", member: "Pump foundation P-101", shape_code: 20, dia_mm: 16, qty: 12, length_mm: 2400 },
      { bar_mark: "P2", member: "Pump foundation P-101", shape_code: 38, dia_mm: 8, qty: 60, length_mm: 1100 },
      { bar_mark: "T1", member: "Tank pad T-101", shape_code: 20, dia_mm: 20, qty: 24, length_mm: 6800 },
    ],
  },
  infrastructure: {
    boq: [
      { code: "EXC_SOFT", qty: 4500, source: "DRAWING" },
      { code: "EMBANK_FILL", qty: 3800, source: "DRAWING" },
      { code: "GSB", qty: 1200, source: "DRAWING" },
      { code: "WMM", qty: 950, source: "DRAWING" },
      { code: "DBM", qty: 420, source: "DRAWING" },
      { code: "BC", qty: 280, source: "DRAWING" },
      { code: "RCC_M30", qty: 320, source: "3D" },
      { code: "REBAR_FE500", qty: 38, source: "3D" },
    ],
    bbs: [
      { bar_mark: "DK1", member: "Bridge Deck D-1", shape_code: 20, dia_mm: 25, qty: 18, length_mm: 11800 },
      { bar_mark: "DK2", member: "Bridge Deck D-1", shape_code: 33, dia_mm: 16, qty: 14, length_mm: 8400 },
      { bar_mark: "DK3", member: "Bridge Deck D-1", shape_code: 38, dia_mm: 10, qty: 110, length_mm: 1600 },
    ],
  },
  real_estate: {
    boq: [
      { code: "RCC_M25", qty: 380, source: "3D" },
      { code: "REBAR_FE500", qty: 42, source: "3D" },
      { code: "EXC_SOFT", qty: 850, source: "DRAWING" },
      { code: "BRICKWORK_230", qty: 220, source: "DRAWING" },
      { code: "PLASTER_INT", qty: 4400, source: "DRAWING" },
      { code: "FLOORING_VITR", qty: 2200, source: "DRAWING" },
      { code: "PAINT_INT", qty: 4800, source: "DRAWING" },
    ],
    bbs: [
      { bar_mark: "B1", member: "Beam B-101", shape_code: 20, dia_mm: 16, qty: 4, length_mm: 5200 },
      { bar_mark: "B2", member: "Beam B-101", shape_code: 33, dia_mm: 16, qty: 2, length_mm: 6400 },
      { bar_mark: "B3", member: "Beam B-101", shape_code: 38, dia_mm: 8, qty: 45, length_mm: 1340 },
      { bar_mark: "C1", member: "Column C-101", shape_code: 20, dia_mm: 20, qty: 8, length_mm: 3600 },
      { bar_mark: "C2", member: "Column C-101", shape_code: 38, dia_mm: 8, qty: 32, length_mm: 1100 },
    ],
  },
};

function Tier1Panel({ projectId, domain = "real_estate" }) {
  const [busy, setBusy] = useState({});
  const [err, setErr] = useState(null);
  const [status, setStatus] = useState({ boq: null, sched: null, bbs: null });

  const setB = (k, v) => setBusy((b) => ({ ...b, [k]: v }));
  const defaults = DELIVERABLE_DEFAULTS[domain] || DELIVERABLE_DEFAULTS.real_estate;

  const runBOQ = async () => {
    setB("boq", true);
    setErr(null);
    try {
      const { data } = await api.post(`/projects/${projectId}/tier1/boq`, {
        design_quantities: defaults.boq,
      });
      setStatus((s) => ({ ...s, boq: { id: data.id, total: data.boq.grand_total } }));
    } catch (e) { setErr(formatErr(e?.response?.data?.detail) || String(e)); }
    finally { setB("boq", false); }
  };

  const runSchedule = async () => {
    setB("sched", true);
    setErr(null);
    try {
      const { data } = await api.post(`/projects/${projectId}/tier1/schedule`, { cost_total: status.boq?.total || 1 });
      setStatus((s) => ({ ...s, sched: { id: data.id, dur: data.cpm.project_duration_days } }));
    } catch (e) { setErr(formatErr(e?.response?.data?.detail) || String(e)); }
    finally { setB("sched", false); }
  };

  const runBBS = async () => {
    setB("bbs", true);
    setErr(null);
    try {
      const { data } = await api.post(`/projects/${projectId}/tier1/bbs`, {
        bars: defaults.bbs,
      });
      setStatus((s) => ({ ...s, bbs: { id: data.id, kg: data.bbs.grand_total_kg } }));
    } catch (e) { setErr(formatErr(e?.response?.data?.detail) || String(e)); }
    finally { setB("bbs", false); }
  };

  const dl = (path, name) => downloadAuthed(`${API_BASE}${path}`, name).catch((e) => setErr(String(e)));

  return (
    <div data-testid="tier1-panel" style={{
      border: "1px solid var(--pf-border)", borderRadius: 12, padding: 18,
      background: "var(--pf-surface)", display: "flex", flexDirection: "column", gap: 14,
    }}>
      <div>
        <div className="pf-h2" style={{ fontSize: 18 }}>Project Deliverables</div>
        <div className="pf-muted" style={{ fontSize: 13 }}>
          BOQ &amp; Cost · CPM Schedule · Tender Documents · Rebar BBS — single click runs use representative line items
          for the <strong>{(domain || "").replace("_", " ")}</strong> domain; replace with your own design quantities via API.
        </div>
      </div>

      {err && <div data-testid="t1-err" style={{
        padding: 10, borderRadius: 6, background: "rgba(198,40,40,0.1)",
        border: "1px solid rgba(198,40,40,0.3)", color: "#FF8888", fontSize: 12,
      }}>{err}</div>}

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
        <button data-testid="t1-run-boq" onClick={runBOQ} disabled={busy.boq}
          className="pf-btn pf-btn-primary"
          style={{ padding: "10px", fontSize: 13, fontWeight: 600, display: "inline-flex", gap: 6, alignItems: "center", justifyContent: "center" }}>
          {busy.boq ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} />}
          Run BOQ + Cost
        </button>
        <button data-testid="t1-run-sched" onClick={runSchedule} disabled={busy.sched}
          className="pf-btn pf-btn-primary"
          style={{ padding: "10px", fontSize: 13, fontWeight: 600, display: "inline-flex", gap: 6, alignItems: "center", justifyContent: "center" }}>
          {busy.sched ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} />}
          Run CPM Schedule
        </button>
        <button data-testid="t1-run-bbs" onClick={runBBS} disabled={busy.bbs}
          className="pf-btn pf-btn-primary"
          style={{ padding: "10px", fontSize: 13, fontWeight: 600, display: "inline-flex", gap: 6, alignItems: "center", justifyContent: "center" }}>
          {busy.bbs ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} />}
          Compose BBS
        </button>
      </div>

      <div data-testid="t1-status" className="pf-muted" style={{ fontSize: 12, lineHeight: 1.7 }}>
        {status.boq && <div>BOQ <code>{status.boq.id}</code> · Grand total {status.boq.total.toLocaleString()}</div>}
        {status.sched && <div>Schedule <code>{status.sched.id}</code> · Duration {status.sched.dur} days</div>}
        {status.bbs && <div>BBS <code>{status.bbs.id}</code> · Total steel {status.bbs.kg} kg</div>}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10 }}>
        <DownloadCard label="BOQ · XLSX" sublabel="Summary + per-discipline + abstract" icon={FileSpreadsheet}
          testid="t1-dl-boq" onClick={() => dl(`/projects/${projectId}/tier1/boq.xlsx`, "BOQ.xlsx")} />
        <DownloadCard label="Tender · DOCX" sublabel="5-volume MoRTH/CPWD format" icon={FileText}
          testid="t1-dl-tender" onClick={() => dl(`/projects/${projectId}/tier1/tender.docx`, "Tender.docx")} />
        <DownloadCard label="Schedule · XLSX" sublabel="CPM with ES/EF/LS/LF/TF/FF" icon={FileSpreadsheet}
          testid="t1-dl-sched" onClick={() => dl(`/projects/${projectId}/tier1/schedule.xlsx`, "Schedule.xlsx")} />
        <DownloadCard label="Gantt · SVG" sublabel="Critical path highlighted" icon={FileText}
          testid="t1-dl-gantt" onClick={() => dl(`/projects/${projectId}/tier1/gantt.svg`, "Gantt.svg")} />
        <DownloadCard label="BBS · XLSX" sublabel="IS 2502 bar bending schedule" icon={FileSpreadsheet}
          testid="t1-dl-bbs" onClick={() => dl(`/projects/${projectId}/tier1/bbs.xlsx`, "BBS.xlsx")} />
      </div>
    </div>
  );
}
